import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { inr } from "@/lib/api";
import AccountSidebar from "@/components/account/AccountSidebar";
import { CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => { api.get(`/orders/${id}`).then(r => setO(r.data)).catch(()=>setO(false)); }, [id]);
  if (o === null) return <div className="min-h-[40vh] grid place-items-center text-slate-500">Loading…</div>;
  if (!o) return <div className="min-h-[40vh] grid place-items-center text-slate-500">Order not found</div>;

  const stages = ["Placed","Confirmed","Packed","Shipped","Out for Delivery","Delivered"];
  const reachedIdx = stages.indexOf(o.status);
  const isCancelled = o.status === "Cancelled";

  const cancel = async () => {
    if (!window.confirm("Cancel this order?")) return;
    await api.post(`/orders/${id}/cancel`);
    const r = await api.get(`/orders/${id}`); setO(r.data);
    toast.success("Order cancelled");
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-[240px_1fr] gap-6">
      <AccountSidebar/>
      <section className="space-y-5">
        <div className="bg-white border border-slate-100 rounded-2xl p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-2xl font-bold">Order Tracking</h1>
              <div className="text-xs text-slate-500 font-mono mt-1">{o.order_id}</div>
            </div>
            {!["Delivered","Cancelled"].includes(o.status) && (
              <button data-testid="cancel-order-btn" onClick={cancel} className="text-sm text-red-600 font-semibold border border-red-200 rounded-full px-4 py-1.5">Cancel Order</button>
            )}
          </div>
          {isCancelled ? (
            <div className="mt-6 bg-red-50 text-red-700 rounded-xl p-4 text-sm font-medium">This order was cancelled.</div>
          ) : (
            <div className="mt-6 grid grid-cols-3 md:grid-cols-6 gap-2">
              {stages.map((s, i) => (
                <div key={s} className="flex flex-col items-center text-center">
                  {i <= reachedIdx ? <CheckCircle2 className="text-emerald-500" size={22}/> : <Circle className="text-slate-300" size={22}/>}
                  <span className={`text-[11px] mt-1 ${i<=reachedIdx?"text-slate-900 font-semibold":"text-slate-400"}`}>{s}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-100 rounded-2xl p-5">
          <h3 className="font-display font-bold mb-3">Items</h3>
          {o.items?.map((it,i) => (
            <div key={i} className="flex gap-3 py-3 border-t border-slate-100 first:border-0">
              <img src={it.image} className="w-16 h-20 rounded-lg object-cover"/>
              <div className="flex-1"><div className="font-medium text-sm">{it.title}</div><div className="text-xs text-slate-500">Qty {it.quantity}{it.size?` · ${it.size}`:""}{it.color?` · ${it.color}`:""}</div></div>
              <div className="font-semibold text-sm">{inr(it.line_total)}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white border border-slate-100 rounded-2xl p-5">
            <h3 className="font-display font-bold mb-3">Delivery Address</h3>
            <div className="text-sm text-slate-700">
              <div className="font-semibold">{o.address?.full_name} · {o.address?.mobile}</div>
              <div className="mt-1">{o.address?.house}, {o.address?.area}, {o.address?.city}, {o.address?.state} - {o.address?.pincode}</div>
            </div>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-5">
            <h3 className="font-display font-bold mb-3">Payment Summary</h3>
            <Row k="Subtotal" v={inr(o.subtotal)}/>
            <Row k="Shipping" v={o.shipping===0?"FREE":inr(o.shipping)}/>
            <div className="border-t my-2 border-slate-100"/>
            <Row k={<b>Total</b>} v={<b className="text-[#004BCA]">{inr(o.total)}</b>}/>
            <div className="text-xs text-slate-500 mt-2">Method: {o.payment_method} · Status: {o.payment_status}</div>
          </div>
        </div>
      </section>
    </main>
  );
}
function Row({k,v}){return <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{k}</span><span>{v}</span></div>;}
